import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, BellOff, Send, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { pushSupported, pushPermission, getPushSubscription, subscribeToPush, unsubscribeFromPush, sendTestPush } from "@/lib/push";

export default function NotifikasiPush() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [endpoint, setEndpoint] = useState("");

  const refresh = async () => {
    const sup = pushSupported();
    setSupported(sup);
    setPermission(pushPermission());
    if (sup) {
      const s = await getPushSubscription();
      setSubscribed(!!s);
      setEndpoint(s?.endpoint || "");
    }
  };
  useEffect(() => { refresh(); }, []);

  const doSubscribe = async () => {
    setLoading(true);
    try {
      await subscribeToPush();
      toast.success("Notifikasi push berhasil diaktifkan");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Gagal aktivasi push");
    } finally { setLoading(false); }
  };

  const doUnsubscribe = async () => {
    if (!window.confirm("Nonaktifkan notifikasi push di perangkat ini?")) return;
    setLoading(true);
    try {
      await unsubscribeFromPush();
      toast.success("Notifikasi push dinonaktifkan");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Gagal");
    } finally { setLoading(false); }
  };

  const doTest = async () => {
    setLoading(true);
    try {
      const r = await sendTestPush();
      toast.success(`Test terkirim: ${r.sent}/${(r.sent||0)+(r.failed||0)} perangkat`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal kirim test");
    } finally { setLoading(false); }
  };

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Sistem • Notifikasi</div>
        <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
          <BellRing className="h-7 w-7 text-secondary" /> Notifikasi Push
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Aktifkan notifikasi push di perangkat Anda supaya pengumuman & pengingat ibadah langsung muncul di HP/desktop, bahkan saat aplikasi tidak sedang dibuka.
        </p>
      </div>

      <div className="max-w-2xl space-y-4">
        {/* Status card */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Dukungan Browser</div>
                <div className={`font-heading font-bold mt-1 ${supported ? "text-emerald-600" : "text-rose-600"}`} data-testid="push-supported">
                  {supported ? "Didukung" : "Tidak didukung"}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Izin</div>
                <div className={`font-heading font-bold mt-1 ${permission === "granted" ? "text-emerald-600" : permission === "denied" ? "text-rose-600" : "text-amber-600"}`} data-testid="push-permission">
                  {permission === "granted" ? "Diizinkan" : permission === "denied" ? "Ditolak" : "Belum Diminta"}
                </div>
              </div>
              <div>
                <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">Perangkat Ini</div>
                <div className={`font-heading font-bold mt-1 ${subscribed ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="push-status">
                  {subscribed ? "Berlangganan" : "Belum"}
                </div>
              </div>
            </div>

            {permission === "denied" && (
              <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-900 text-xs p-3 flex items-start gap-2" data-testid="push-denied-hint">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  Izin notifikasi ditolak. Buka pengaturan browser (klik ikon gembok di address bar → Notifikasi → Izinkan), lalu muat ulang halaman ini.
                </div>
              </div>
            )}

            {!supported && (
              <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs p-3 flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  Browser Anda tidak mendukung Web Push. Gunakan Chrome, Edge, Firefox, atau Safari 16+ di iOS 16.4+.
                </div>
              </div>
            )}

            {supported && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                {!subscribed ? (
                  <Button onClick={doSubscribe} disabled={loading || permission === "denied"} className="rounded-full" data-testid="btn-push-subscribe">
                    <BellRing className="h-4 w-4 mr-2" /> {loading ? "Mengaktifkan…" : "Aktifkan Notifikasi"}
                  </Button>
                ) : (
                  <>
                    <Button onClick={doTest} disabled={loading} className="rounded-full" data-testid="btn-push-test">
                      <Send className="h-4 w-4 mr-2" /> Kirim Test
                    </Button>
                    <Button onClick={doUnsubscribe} disabled={loading} variant="outline" className="rounded-full" data-testid="btn-push-unsubscribe">
                      <BellOff className="h-4 w-4 mr-2" /> Nonaktifkan
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info card */}
        <Card>
          <CardContent className="p-6 space-y-3 text-sm">
            <div className="flex items-center gap-2 font-heading font-bold">
              <ShieldCheck className="h-4 w-4 text-primary" /> Kapan notifikasi ini muncul?
            </div>
            <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
              <li><strong>Broadcast Pengumuman</strong> — saat Sekretariat mengirim pengumuman dengan channel Push aktif</li>
              <li><strong>Reminder Ibadah</strong> — pengingat ibadah khusus (fitur berikutnya)</li>
              <li><strong>Kegiatan Baru</strong> — saat kegiatan publik baru dipublikasikan (fitur berikutnya)</li>
            </ul>
            <div className="text-[0.7rem] text-muted-foreground pt-2 border-t border-border">
              Notifikasi hanya dikirim ke perangkat yang berlangganan. Anda dapat mengaktifkannya di setiap perangkat (HP, laptop) untuk menerima notifikasi di semua tempat.
            </div>
          </CardContent>
        </Card>

        {subscribed && endpoint && (
          <div className="text-[0.65rem] text-muted-foreground font-mono break-all" data-testid="push-endpoint-preview">
            Endpoint: {endpoint.slice(0, 80)}…
          </div>
        )}
      </div>
    </AppShell>
  );
}
